import { formatDate } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import { AgGridAngular } from 'ag-grid-angular';
import {
  CellClickedEvent,
  ColDef,
  ICellRendererParams,
} from 'ag-grid-community';
import { distinctUntilKeyChanged } from 'rxjs/operators';
import { CohortApprovalModalComponent } from './cohort-approval-modal/cohort-approval-modal.component';
import { CohortStatusCellComponent } from './cohort-status-cell/cohort-status-cell.component';
import { CreateCohortComponent } from './create-cohort/create-cohort.component';
import {
  Cohort,
  CohortQuery,
  CohortService,
  CohortStatus,
} from './services/cohort.service';
import { SelectedCohortService } from './services/selected-cohort/selected-cohort.service';

@Component({
  selector: 'dcc-cohorts-page',
  templateUrl: './cohorts.component.html',
  styleUrls: ['./cohorts.component.scss'],
  providers: [SelectedCohortService],
})
export class CohortsComponent {
  @ViewChild('cohortsGrid') grid!: AgGridAngular;

  cohorts: Cohort[] = [];
  pagedCohorts: { [key: number]: Cohort[] } = {};

  query: CohortQuery = {
    name: '',
    creator: '',
    status: [CohortStatus.AwaitingApproval, CohortStatus.Enabled],
  };

  currentPage = 1;
  pageCount = 1;
  pageSize = 50;

  defaultColDef: ColDef = {
    sortable: true,
    filter: false,
    resizable: true,
    suppressMovable: true,
  };

  columns: ColDef[] = [
    {
      field: 'name',
      headerName: 'Cohort',
      width: 300,
      maxWidth: 300,
      cellStyle: { cursor: 'pointer', color: '#0d6efd' },
      onCellClicked: (e: CellClickedEvent<Cohort>) => {
        const selectionContext = this.selectedCohortService.selectCohort(
          e.data,
          true,
        );

        selectionContext.closed.subscribe(async () => {
          await this.getData();
        });
      },
    },
    {
      maxWidth: 129,
      minWidth: 129,
      cellRenderer: this.approvalButtonRender,
      onCellClicked: (event: CellClickedEvent<Cohort>) => {
        if (event.data.hasPendingApproval) {
          const modal = this.modalService.open(CohortApprovalModalComponent);

          modal.componentInstance.cohort = event.data;

          modal.closed.subscribe(async () => await this.getData());
        }
      },
    },
    {
      headerName: 'Status',
      cellRenderer: CohortStatusCellComponent,
    },
    {
      headerName: 'Created Date',
      cellRenderer: (params: ICellRendererParams<Cohort>) =>
        this.dateRender(params.data.createdDate),
    },
    {
      headerName: 'Enabled Date',
      cellRenderer: (params: ICellRendererParams<Cohort>) =>
        this.dateRender(params.data.activeDate),
    },
    {
      headerName: 'Disabled Date',
      cellRenderer: (params: ICellRendererParams<Cohort>) =>
        this.dateRender(params.data.disabledDate),
    },
    {
      field: 'cohortCreator',
      headerName: 'Owner',
    },
  ];

  constructor(
    private cohortService: CohortService,
    private modalService: NgbModal,
    private modalConfig: NgbModalConfig,
    private route: ActivatedRoute,
    private router: Router,
    private selectedCohortService: SelectedCohortService,
  ) {
    modalConfig.size = 'lg';
    modalConfig.windowClass = 'lytxModal';
    modalConfig.centered = true;

    this.route.queryParams
      .pipe(distinctUntilKeyChanged('query'))
      .subscribe((params) => {
        const query = params['query'];

        if (query) {
          this.query = JSON.parse(query);
        }

        this.getData();
      });
  }

  createCohort() {
    this.modalService
      .open(CreateCohortComponent)
      .closed.subscribe(async () => await this.getData());
  }

  updateQuery() {
    this.updateQueryParams();
  }

  selectPageSize(pageSize: number): void {
    this.pageSize = pageSize;
    this.selectPage();
    this.setPaging();
  }

  selectPage(pageNumber = 1) {
    this.currentPage = pageNumber;
  }

  private async getData(): Promise<void> {
    this.selectPage();
    this.grid?.api.showLoadingOverlay();

    this.cohorts = (await this.cohortService.getCohorts()).filter(
      (cohort) =>
        (!this.query ||
          !this.query.name ||
          cohort.name.toLowerCase().includes(this.query.name.toLowerCase())) &&
        (!this.query ||
          !this.query.status.length ||
          this.query.status.includes(cohort.status) ||
          (this.query.status.includes(CohortStatus.AwaitingApproval) &&
            cohort.hasPendingApproval)) &&
        (!this.query ||
          !this.query.creator ||
          (cohort.cohortCreator &&
            cohort.cohortCreator
              .toLowerCase()
              .includes(this.query.creator.toLowerCase()))),
    );

    this.setPaging();
    this.grid?.api.hideOverlay();
    this.grid?.api.sizeColumnsToFit();
  }

  private setPaging() {
    this.pageCount = Math.ceil(this.cohorts.length / this.pageSize);

    this.pagedCohorts = this.cohorts.reduce((acc, cohort, index) => {
      const page = Math.floor(index / this.pageSize) + 1;
      if (!acc[page]) {
        acc[page] = [];
      }
      acc[page].push(cohort);
      return acc;
    }, {});

    this.selectPage();
  }

  private updateQueryParams(): Promise<boolean> {
    const query: string =
      Object.keys(this.query).length > 0 ? JSON.stringify(this.query) : '';

    return this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        query: query,
      },
    });
  }

  private dateRender(date: string): string {
    return date ? formatDate(date, 'short', 'en-US') : '';
  }

  private approvalButtonRender(
    params: ICellRendererParams<Cohort>,
  ): HTMLElement {
    if (params.data.hasPendingApproval) {
      const button = document.createElement('button');
      button.textContent = 'Review';
      button.className = 'lx-btn-small lx-btn-negative';
      button.style.cssText = 'min-width: 0px;';
      return button;
    }
    return document.createElement('span');
  }
}
